
const getPagination = ({ totalItems = 0, pageSize = 10, currentPage = 1 }) => {
    const totalPages = Math.ceil(totalItems / pageSize);
    
}

/**
 * Serializa el multi-sort de Vuetify al formato que espera el backend.
 *
 * El `v-data-table` de Vuetify 2 mantiene `options.sortBy` y `options.sortDesc`
 * como arrays PARALELOS: `sortBy: ['nombre', 'clave']` con
 * `sortDesc: [false, true]` significa "por nombre ascendente, luego por clave
 * descendente".
 *
 * El formato de transporte es un único parámetro `sort` con pares
 * `campo:dirección` separados por comas:
 *
 *   ?sort=nombre:asc,clave:desc
 *
 * El backend lo parsea con un `split(",")` y, por cada par, un `split(":")`.
 * Cuando no hay sort, el parámetro no se envía: los endpoints que no lo
 * soportan (o que usan `PageRequest.of(page, size)` sin `Sort`) lo ignoran.
 *
 * @param {string[]} sortBy   campos por los que ordenar
 * @param {boolean[]} sortDesc dirección de cada campo (true = descendente)
 * @returns {string} el valor del parámetro `sort`, o '' si no hay nada que ordenar
 */
const buildSortParam = (sortBy = [], sortDesc = []) => {
    const fields = Array.isArray(sortBy) ? sortBy : [sortBy].filter(Boolean);
    if (!fields.length) return '';

    const directions = Array.isArray(sortDesc) ? sortDesc : [sortDesc];

    // El índice de `directions` tiene que corresponder al campo, así que los
    // campos vacíos se descartan ANTES de emparejar: si se filtraran después,
    // `sortBy: ['', 'clave']` con `sortDesc: [false, true]` emparejaría
    // 'clave' con la dirección de '' (asc) en vez de con la suya (desc).
    return fields
        .map((field, index) => ({ field, desc: !!directions[index] }))
        .filter(({ field }) => !!field)
        .map(({ field, desc }) => `${field}:${desc ? 'desc' : 'asc'}`)
        .join(',');
}

/**
 * Normaliza la respuesta paginada del backend.
 *
 * El backend es inconsistente: la mayoría de los endpoints devuelven
 * `totalElements`, pero `UsuarioController` devuelve `totalItem`. Además
 * algunas respuestas traen `currentPage` en base 0 (como el `page` que
 * mandamos) y otras en base 1. Esta función normaliza ambos casos.
 *
 * @param {object} data el cuerpo de la respuesta
 * @returns {{ rows: Array, total: number, totalPages: number, currentPage: number }}
 */
const normalizePage = (data = {}) => {
    const rows = data.rows || data.content || [];
    const total = data.totalElements ?? data.totalItem ?? data.total ?? 0;
    const totalPages = data.totalPages ?? 0;
    // `currentPage` llega en base 0 (igual que el `page` que mandamos).
    // La tabla de Vuetify trabaja en base 1, de ahí el +1.
    const currentPage = data.currentPage != null ? data.currentPage + 1 : 1;

    return { rows, total, totalPages, currentPage };
}

const getParamsPagination = ({ options, search = '', params = {} }) => {
    const { page, itemsPerPage, sortBy, sortDesc } = options || {};
    const sort = buildSortParam(sortBy, sortDesc);

    const query = {
        page: (page > 0 ? page - 1 : 0),
        itemsPerPage: (itemsPerPage > 0 ? itemsPerPage : 25),
        search,
        ...params
    };

    // El parámetro `sort` solo se manda si hay algo que ordenar: mandarlo
    // vacío en todos los endpoints sería ruido en la URL y obligaría al
    // backend a parsearlo siempre.
    if (sort) query.sort = sort;

    return query;
}

export  { getPagination, getParamsPagination, buildSortParam, normalizePage }
